import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase-client';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import { CHOOSE_OPTION_LABEL, getFormFieldPlaceholder } from '../../../utils/formFieldPresentation';

import QualificationProfileField from '../../teams/qualifications/QualificationProfileField';
import { useQualificationRecords } from '../../teams/qualifications/useQualificationRecords';

const updateMemberProfileFieldsFn = httpsCallable(functions, 'updateMemberProfileFields');

export default function MemberProfileFieldsForm({
  teamId,
  userId,
  memberName = '',
  mode = 'self',
  isOpen,
  onClose,
  config
}) {
  const [fieldsConfig, setFieldsConfig] = useState(null);
  const [values, setValues] = useState({});
  const [profileUpdateRequested, setProfileUpdateRequested] = useState(false);
  const [profileUpdateRequestedAt, setProfileUpdateRequestedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const qualificationRefs = useRef({});
  const isAdminMode = mode === 'admin';
  useEffect(() => {
    if (!isOpen || !teamId || !userId) return;
    async function fetchData() {
      setLoading(true);
      setError('');
      try {
        let cfg = config;
        if (!cfg) {
          const cfgSnap = await getDoc(doc(db, 'teams', teamId, 'profileFields', 'config'));
          cfg = cfgSnap.exists() ? cfgSnap.data() : {
            enabled: false,
            fields: []
          };
        }
        setFieldsConfig(cfg);
        const memberSnap = await getDoc(doc(db, 'teams', teamId, 'members', userId));
        const memberData = memberSnap.exists() ? memberSnap.data() || {} : {};
        const existing = memberData?.profileFields || {};
        setValues(existing);
        setProfileUpdateRequested(Boolean(memberData?.profileUpdateRequestOpen));
        setProfileUpdateRequestedAt(memberData?.profileUpdateRequestedAt || null);
      } catch (err) {
        setError('Failed to load profile fields.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [isOpen, teamId, userId, config]);
  const fields = useMemo(() => {
    const list = Array.isArray(fieldsConfig?.fields) ? fieldsConfig.fields : [];
    return fieldsConfig?.enabled === false ? [] : list;
  }, [fieldsConfig]);
  const qualificationData = useQualificationRecords(teamId, userId, isOpen && fields.some(field => field.type === 'qualification'));
  const requestedLabel = useMemo(() => {
    if (!profileUpdateRequestedAt?.toDate) return '';
    try {
      return profileUpdateRequestedAt.toDate().toLocaleDateString();
    } catch {
      return '';
    }
  }, [profileUpdateRequestedAt]);
  const handleChange = (id, value) => {
    setValues(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {};
      for (const field of fields.filter(field => field.type === 'qualification')) await qualificationRefs.current[field.id]?.save();
      fields.filter(field => field.type !== 'qualification').forEach(field => {
        payload[field.id] = values[field.id] ?? '';
      });
      if (isAdminMode) {
        await updateMemberProfileFieldsFn({
          teamId,
          memberId: userId,
          profileFields: payload
        });
      } else {
        await setDoc(doc(db, 'teams', teamId, 'members', userId), {
          profileFields: payload,
          profileFieldsUpdatedAt: serverTimestamp(),
          profileUpdateRequestOpen: false,
          profileUpdateRequestedAt: null
        }, {
          merge: true
        });
        setProfileUpdateRequested(false);
        setProfileUpdateRequestedAt(null);
      }
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save profile fields.');
    } finally {
      setSaving(false);
    }
  };
  if (!isOpen) return null;
  return <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content member-profile-fields-modal" onClick={e => e.stopPropagation()}>
        <h2>{isAdminMode ? 'Edit Profile Details' : 'Update Profile Fields'}</h2>
        {isAdminMode && memberName && <p className="text-muted member-profile-fields-modal__member">
            Update club-specific profile details for <strong>{memberName}</strong>.
          </p>}
        {error && <ErrorMessage message={error} />}
        {loading ? <p>Loading...</p> : fields.length === 0 ? <p className="text-muted">No profile fields are configured for this team.</p> : <form onSubmit={handleSubmit}>
            {profileUpdateRequested && <div style={{
          marginBottom: '1rem',
          padding: '0.9rem 1rem',
          borderRadius: '12px',
          background: 'rgba(2, 132, 199, 0.08)',
          border: '1px solid rgba(2, 132, 199, 0.18)'
        }}>
                <div style={{
              fontWeight: 600,
              marginBottom: '0.25rem'
            }}>
                  Update requested
                </div>
                <div className="text-muted">
                  {isAdminMode
                    ? `The member still needs to personally review and confirm this request${requestedLabel ? ` from ${requestedLabel}` : ''}.`
                    : `Review these details and save to confirm the update${requestedLabel ? ` from ${requestedLabel}` : ''}.`}
                </div>
              </div>}
            {fields.map(field => {
          if (field.type === 'qualification') return <QualificationProfileField key={field.id} ref={instance => { qualificationRefs.current[field.id] = instance; }} teamId={teamId} memberId={userId} field={field} record={qualificationData.records.find(record => record.qualificationId === field.id)} isAdmin={isAdminMode} loaded={qualificationData.loaded} loadError={qualificationData.error} />;
          const label = `${field.label}${field.required ? ' *' : ''}`;
          const value = values[field.id] ?? '';
          const inputId = `member-profile-field-${field.id}`;
          if (field.type === 'select') {
            const options = Array.isArray(field.options) ? field.options : [];
            return <div key={field.id} className="form-group">
                    <label htmlFor={inputId}>{label}</label>
                    <select id={inputId} value={value} onChange={e => handleChange(field.id, e.target.value)} required={field.required} className="form-input">
                      <option value="">{CHOOSE_OPTION_LABEL}</option>
                      {options.map(opt => <option key={opt} value={opt}>
                          {opt}
                        </option>)}
                    </select>
                  </div>;
          }
          return <div key={field.id} className="form-group">
                  <label htmlFor={inputId}>{label}</label>
                  <input id={inputId} type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} value={value} onChange={e => handleChange(field.id, e.target.value)} required={field.required} className="form-input" placeholder={getFormFieldPlaceholder(field)} />
                </div>;
        })}

            <div className="form-actions" style={{
          marginTop: '1.5rem',
          display: 'flex',
          gap: '1rem'
        }}>
              <SubmitButton type="button" onClick={onClose} label="Cancel" variant="ghost" size="sm" />
              <SubmitButton
                type="submit"
                label={saving ? 'Saving...' : isAdminMode ? 'Save Profile' : profileUpdateRequested ? 'Confirm Update' : 'Save'}
                disabled={saving}
                variant="primary"
              />
            </div>
          </form>}
      </div>
    </div>;
}
MemberProfileFieldsForm.propTypes = {
  teamId: PropTypes.string.isRequired,
  userId: PropTypes.string,
  memberName: PropTypes.string,
  mode: PropTypes.oneOf(['self', 'admin']),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  config: PropTypes.object
};
